import { bookedWeeksFromDays, type OccupancySeasonHeatmapCell } from '@sea-snag/shared';

import { columnIndexFor } from './heatmap-columns';

/**
 * The occupancy grid's rows, in the order they were given.
 *
 * Extracted from the grid component because the order is the point. The grid
 * used to re-sort these itself, descending by booked weeks, which silently
 * overrode the sort control above it: "most booked weeks" looked correct because
 * it happened to match, while "fewest booked" and "name A–Z" did nothing. The
 * sort function had tests; that the grid honoured it did not, so nothing caught
 * it until it was on staging.
 *
 * Ordering now belongs entirely to the caller. This builds rows and counts and
 * preserves the sequence it receives.
 */

/** Tuesday — the midpoint of a Sat→Fri charter week, matching weekMidForColumn. */
function midOfCharterWeek(weekStart: string): string {
  return new Date(Date.parse(`${weekStart}T00:00:00Z`) + 3 * 86_400_000).toISOString().slice(0, 10);
}

export function countBookedWeeks(
  cells: OccupancySeasonHeatmapCell[],
  isInSeasonColumn: (weekStart: string) => boolean
): { inSeason: number; total: number } {
  let inSeasonDays = 0;
  let totalDays = 0;
  for (const cell of cells) {
    if (cell.bookedDays <= 0) continue; // booked = confirmed only; option/maint/sleepaboard excluded
    totalDays += cell.bookedDays;
    // Judged on the week's midpoint, the same day the season band is shaded on.
    // Testing the Saturday instead made a boundary week count as in-season while
    // the grid showed it outside the band.
    if (isInSeasonColumn(midOfCharterWeek(cell.weekStart))) inSeasonDays += cell.bookedDays;
  }
  return {
    inSeason: bookedWeeksFromDays(inSeasonDays),
    total: bookedWeeksFromDays(totalDays),
  };
}

export interface HeatmapRow<T> {
  boat: T;
  /** Cells keyed by week column. */
  inner: Map<number, OccupancySeasonHeatmapCell>;
  counts: { inSeason: number; total: number };
}

export function buildHeatmapRows<T extends { pid: string; name: string }>(
  boats: T[],
  heatmap: OccupancySeasonHeatmapCell[],
  isInSeasonColumn: (weekStart: string) => boolean,
  year: number
): Array<HeatmapRow<T>> {
  const cellsByBoat = new Map<string, Map<number, OccupancySeasonHeatmapCell>>();
  for (const cell of heatmap) {
    const col = columnIndexFor(cell.weekStart, year);
    if (col === null) continue;
    const inner = cellsByBoat.get(cell.boatPid) ?? new Map<number, OccupancySeasonHeatmapCell>();
    inner.set(col, cell);
    cellsByBoat.set(cell.boatPid, inner);
  }

  // No sort: the caller's order is the order. A boat with no cells keeps its row
  // so the fleet count on the page matches the rows beneath it.
  return boats.map((boat) => {
    const inner = cellsByBoat.get(boat.pid) ?? new Map<number, OccupancySeasonHeatmapCell>();
    return { boat, inner, counts: countBookedWeeks([...inner.values()], isInSeasonColumn) };
  });
}
